import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { Icon, Input, Popup } from 'semantic-ui-react';
import * as S from './styles';
import { fileTypeProps } from '../../../../../../components/UploadFilesUncontroled';
import { CreateClaimEstateInitialInitialValuesType } from '../../Formik/types/CreateClaimEstateInitialValuesTypes';
import { InsurerState } from '../../../../../../types/InsurerState';
import { ClaimPropertyStatusEnum } from '../../../../../../enums/claim-property-status.enum';
import { warningNotification } from '../../../../../../services/notification';
import {
  buildFileLabelFromFilename,
  FILE_LABEL_MAX
} from '../../../../../../utils/buildFileLabelFromFilename';

export const OTHER_FILE_KEY = 'OTHER';

type Properties = {
  fileIdentifiers: fileTypeProps[];
  insurers?: InsurerState[] | null;
  existingFiles?: any[];
  onDeleteExisting?: (id: string) => void;
  /** Optimistic local update of an existing file label (parent state). */
  onExistingLabelChange?: (id: string, label: string) => void;
  /** Persist label for an already-saved file (PATCH). */
  onUpdateExistingLabel?: (id: string, label: string) => Promise<void> | void;
  highlightMissingLabels?: boolean;
};

const DocumentsComponent = ({
  fileIdentifiers,
  values,
  setFieldValue,
  insurers,
  existingFiles,
  onDeleteExisting,
  onExistingLabelChange,
  onUpdateExistingLabel,
  highlightMissingLabels = false
}: FormikProps<CreateClaimEstateInitialInitialValuesType> & Properties) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeUploadKey, setActiveUploadKey] = useState<string | null>(null);
  const [savingLabelId, setSavingLabelId] = useState<string | null>(null);
  const documentsOptional = !fileIdentifiers.some(item => item.required === true);

  const handleUploadClick = (key: string) => {
    setActiveUploadKey(key);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleDeleteClick = (key: string, indexToDelete: number) => {
    if (window.confirm('Tem certeza que deseja remover este arquivo?')) {
      const filesForKey = values.claimFiles.filter(f => f.name === key);
      const otherFiles = values.claimFiles.filter(f => f.name !== key);
      const newFilesForKey = filesForKey.filter((_, index) => index !== indexToDelete);
      setFieldValue('claimFiles', [...otherFiles, ...newFilesForKey]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && activeUploadKey) {
      const file = files[0];
      if (file.size > 50 * 1024 * 1024) {
        warningNotification('Arquivo muito grande', 'O arquivo excede o limite de 50MB.');
        return;
      }

      const newFileEntry = {
        file,
        name: activeUploadKey,
        customLabel:
          activeUploadKey === OTHER_FILE_KEY
            ? buildFileLabelFromFilename(file.name)
            : undefined
      };
      setFieldValue('claimFiles', [...values.claimFiles, newFileEntry]);
    }
    setActiveUploadKey(null);
  };

  const updateClaimFileLabel = (key: string, index: number, label: string) => {
    const filesForKey = values.claimFiles.filter(f => f.name === key);
    const otherFiles = values.claimFiles.filter(f => f.name !== key);
    const updatedForKey = filesForKey.map((f, i) =>
      i === index ? { ...f, customLabel: label.slice(0, FILE_LABEL_MAX) } : f
    );
    setFieldValue('claimFiles', [...otherFiles, ...updatedForKey]);
  };

  const handleExistingLabelBlur = async (fileId: string, label: string) => {
    if (!onUpdateExistingLabel) return;
    const trimmed = label.trim();
    if (!trimmed) return;
    try {
      setSavingLabelId(fileId);
      await onUpdateExistingLabel(fileId, trimmed.slice(0, FILE_LABEL_MAX));
    } finally {
      setSavingLabelId(null);
    }
  };

  const getFilesByKey = (key: string) => {
    return values.claimFiles.filter(f => f.name === key);
  };

  const getOccupationLabel = () => {
    if (values.claimProperty.status === ClaimPropertyStatusEnum.occupied)
      return 'Ocupado';
    if (values.claimProperty.status === ClaimPropertyStatusEnum.vacant)
      return 'Desocupado';
    return '-';
  };

  const getInsurerName = () => {
    if (values.claimEstate.insurerId && insurers) {
      const insurer = insurers.find(i => i.id === values.claimEstate.insurerId);
      if (insurer) return insurer.name;
    }
    return '[Nome da seguradora]';
  };

  const renderOtherLabelInput = ({
    value,
    onChange,
    onBlur,
    invalid,
    loading
  }: {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    invalid?: boolean;
    loading?: boolean;
  }) => (
    <S.LabelInputWrapper>
      <Input
        fluid
        size="small"
        value={value}
        maxLength={FILE_LABEL_MAX}
        placeholder="Ex.: Recibo IPTU"
        loading={loading}
        error={invalid}
        onChange={(_e, { value: next }) => onChange(String(next || ''))}
        onBlur={onBlur}
      />
      <S.LabelCounter>
        {(value || '').length}/{FILE_LABEL_MAX}
      </S.LabelCounter>
    </S.LabelInputWrapper>
  );

  return (
    <S.Container>
      <S.Header>
        <S.TitleBlock>
          <S.Title>
            {documentsOptional
              ? 'A inserção de documentos é opcional nesta abertura de Sinistro.'
              : 'A inserção de documentos é obrigatória para abertura do Sinistro.'}
          </S.Title>
          <S.SubTitle>
            {documentsOptional ? (
              <>
                Você pode anexar os documentos abaixo para auxiliar na análise do
                sinistro, mas nenhum deles é obrigatório para solicitar a abertura. São
                aceitos arquivos de até 50MB. Formatos aceitos: jpeg, png, webp, doc,
                docx, PDF e odt
                <br />
                <b>Nota:</b> Você pode adicionar múltiplos arquivos para cada tipo de
                documento.
              </>
            ) : (
              <>
                Verifique a lista de documentos abaixo para enviar corretamente os
                documentos exigidos de acordo com a ocupação do imóvel e Seguradora
                selecionada. São aceitos arquivos de até 50MB. Formatos aceitos: jpeg,
                png, webp, doc, docx, PDF e odt
                <br />
                <b>Nota:</b> Você pode adicionar múltiplos arquivos para cada tipo de
                documento.
              </>
            )}
          </S.SubTitle>
        </S.TitleBlock>
        <S.InfoBanner>
          <Icon name="info circle" />
          <span>
            {documentsOptional ? (
              <>
                Se preferir, você pode seguir com a solicitação sem anexos e realizar o
                upload da documentação em outro momento pelo gerenciamento de anexos do
                sinistro.
              </>
            ) : (
              <>
                Caso não tenha acesso a toda a documentação, você pode seguir com a{' '}
                <b>criação do processo de Sinistro no formato de Rascunho</b> e realizar
                o upload da documentação em outro momento
              </>
            )}
          </span>
        </S.InfoBanner>
      </S.Header>

      <S.SectionWithBorder>
        <S.InfoSection>
          {!documentsOptional && (
            <S.InfoColumn>
              <S.InfoLabel>Status de ocupação do imóvel</S.InfoLabel>
              <S.InfoValueBox>
                <S.InfoValueText>{getOccupationLabel()}</S.InfoValueText>
              </S.InfoValueBox>
            </S.InfoColumn>
          )}
          <S.InfoColumn>
            <S.InfoLabel>Seguradora/Garantidora</S.InfoLabel>
            <S.InfoValueBox>
              <S.InfoValueText>{getInsurerName()}</S.InfoValueText>
            </S.InfoValueBox>
          </S.InfoColumn>
        </S.InfoSection>

        <S.DocumentsListContainer>
          <S.ListHeader>
            <S.ListHeaderLabel className="col-name">
              <Icon name="fire" style={{ color: '#59249B' }} />
              {documentsOptional ? 'Arquivos opcionais' : 'Arquivos necessários'}
            </S.ListHeaderLabel>
            <S.ListHeaderLabel className="col-status">Status</S.ListHeaderLabel>
            <S.ListHeaderLabel className="col-actions">Ações</S.ListHeaderLabel>
          </S.ListHeader>

          {fileIdentifiers.map(item => {
            const uploadedFiles = getFilesByKey(item.key);
            const currentExistingFiles =
              existingFiles?.filter(f => f.key === item.key || f.option === item.key) ||
              [];
            const hasFiles = uploadedFiles.length > 0 || currentExistingFiles.length > 0;
            const isRequired = item.required === true;
            const isOther = item.key === OTHER_FILE_KEY;

            return (
              <React.Fragment key={item.key}>
                {isOther && hasFiles && (
                  <S.OtherHelper>
                    Dê um nome curto para facilitar a identificação deste arquivo.
                  </S.OtherHelper>
                )}

                {currentExistingFiles.map((existingFile, index) => {
                  const labelValue = existingFile.label || '';
                  const invalid =
                    highlightMissingLabels && isOther && !String(labelValue).trim();

                  return (
                    <S.ListItem key={`existing-${existingFile.id}`}>
                      <S.DocumentName>
                        <S.DocumentIconWrapper>
                          <Icon name="file text outline" style={{ color: '#59249B' }} />
                        </S.DocumentIconWrapper>
                        {isOther ? (
                          <S.OtherLabelBlock>
                            <a
                              href={existingFile.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                color: '#888',
                                fontSize: 11,
                                textDecoration: 'none'
                              }}
                            >
                              Outros · Anexado {index + 1}
                            </a>
                            {renderOtherLabelInput({
                              value: labelValue,
                              invalid,
                              loading: savingLabelId === existingFile.id,
                              onChange: next =>
                                onExistingLabelChange?.(existingFile.id, next),
                              onBlur: () =>
                                handleExistingLabelBlur(existingFile.id, labelValue)
                            })}
                          </S.OtherLabelBlock>
                        ) : (
                          <>
                            <a
                              href={existingFile.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: 'inherit', textDecoration: 'none' }}
                            >
                              {item.name} (Anexado - {index + 1})
                            </a>
                            {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                          </>
                        )}
                      </S.DocumentName>
                      <S.StatusWrapper>
                        <S.StatusBadge status="sent">
                          <S.StatusDot status="sent" />
                          <S.StatusText>Anexado</S.StatusText>
                        </S.StatusBadge>
                      </S.StatusWrapper>
                      <S.ActionsWrapper>
                        <S.ActionButton
                          actionType="delete"
                          onClick={() =>
                            onDeleteExisting && onDeleteExisting(existingFile.id)
                          }
                          title="Remover arquivo salvo"
                        >
                          <Icon name="times" style={{ margin: 0 }} />
                        </S.ActionButton>
                      </S.ActionsWrapper>
                    </S.ListItem>
                  );
                })}

                {uploadedFiles.map((uploadedFile, index) => {
                  const labelValue = uploadedFile.customLabel || '';
                  const invalid =
                    highlightMissingLabels && isOther && !String(labelValue).trim();

                  return (
                    <S.ListItem key={`${item.key}-${index}`}>
                      <S.DocumentName>
                        <S.DocumentIconWrapper>
                          <Icon name="file text outline" style={{ color: '#59249B' }} />
                        </S.DocumentIconWrapper>
                        {isOther ? (
                          <S.OtherLabelBlock>
                            <span style={{ color: '#888', fontSize: 11 }}>
                              Outros · Novo {index + 1}
                            </span>
                            {renderOtherLabelInput({
                              value: labelValue,
                              invalid,
                              onChange: next =>
                                updateClaimFileLabel(item.key, index, next)
                            })}
                          </S.OtherLabelBlock>
                        ) : (
                          <>
                            {item.name} ({index + 1})
                            {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                          </>
                        )}
                      </S.DocumentName>
                      <S.StatusWrapper>
                        <S.StatusBadge status="sent">
                          <S.StatusDot status="sent" />
                          <S.StatusText>Enviado</S.StatusText>
                        </S.StatusBadge>
                      </S.StatusWrapper>
                      <S.ActionsWrapper>
                        <S.ActionButton
                          actionType="delete"
                          onClick={() => handleDeleteClick(item.key, index)}
                          title="Remover arquivo"
                        >
                          <Icon name="times" style={{ margin: 0 }} />
                        </S.ActionButton>
                      </S.ActionsWrapper>
                    </S.ListItem>
                  );
                })}

                <S.ListItem key={`${item.key}-add`}>
                  <S.DocumentName>
                    <S.DocumentIconWrapper>
                      <Icon
                        name="file text outline"
                        style={{
                          color: hasFiles ? '#59249B' : isRequired ? '#FF3B30' : '#999'
                        }}
                      />
                    </S.DocumentIconWrapper>
                    {item.name} {hasFiles ? '(Adicionar Novo)' : ''}
                    {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                    {item.tooltip && (
                      <Popup
                        content={item.tooltip}
                        position="right center"
                        trigger={
                          <Icon
                            name="info circle"
                            style={{ marginLeft: 8, color: '#59249B', cursor: 'help' }}
                          />
                        }
                      />
                    )}
                  </S.DocumentName>
                  <S.StatusWrapper>
                    {!hasFiles && (
                      <S.StatusBadge status="pending">
                        <S.StatusDot status="pending" />
                        <S.StatusText>
                          {isRequired
                            ? 'Obrigatório'
                            : documentsOptional
                              ? 'Opcional'
                              : 'Pendente'}
                        </S.StatusText>
                      </S.StatusBadge>
                    )}
                  </S.StatusWrapper>
                  <S.ActionsWrapper>
                    <S.ActionButton
                      actionType="upload"
                      onClick={() => handleUploadClick(item.key)}
                      title="Enviar arquivo"
                    >
                      <Icon name="upload" style={{ margin: 0 }} />
                    </S.ActionButton>
                  </S.ActionsWrapper>
                </S.ListItem>
              </React.Fragment>
            );
          })}
        </S.DocumentsListContainer>
      </S.SectionWithBorder>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept=".jpeg,.jpg,.png,.webp,.doc,.docx,.pdf,.odt"
      />
    </S.Container>
  );
};

export default DocumentsComponent;
